import { Fragment } from 'react';

import { makeStyles } from '@material-ui/core';

import classNames from 'classnames';

import { extractLastNameComponent } from '@spotify/confidence-core-react';
import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
  diffItems,
  pluralize,
} from '@spotify/plugin-confidence-activity-react';

import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    '&:not(:last-child)': {
      marginBottom: 'var(--bui-space-2)',
    },
  },
  tagContainer: {
    display: 'flex',
    gap: 'var(--bui-space-1)',
  },
});

interface Treatment {
  name?: string;
  weight: number;
  variant?: {
    name: string;
  };
  displayName?: string;
  description?: string;
  imageUri?: string;
}

interface TreatmentsData {
  flag?: string;
  treatments?: Treatment[];
}

const getTreatmentKey = (t: Treatment) => t.name || t.displayName || '';

const getFlagDisplayName = (flagName: string | undefined) => {
  if (!flagName) return '';
  return extractLastNameComponent(flagName) ?? flagName;
};

const TreatmentChangeRender = ({ change }: ActivityChangeOptions) => {
  const classes = useStyles();

  const oldValue = change.oldValue as TreatmentsData | null;
  const newValue = change.newValue as TreatmentsData | null;
  const oldTreatments = oldValue?.treatments ?? [];
  const newTreatments = newValue?.treatments ?? [];

  const { added: addedTreatments, removed: removedTreatments } = diffItems(
    oldTreatments,
    newTreatments,
    getTreatmentKey,
  );

  const weightChanges = newTreatments
    .map(newT => {
      const oldT = oldTreatments.find(
        t => getTreatmentKey(t) === getTreatmentKey(newT),
      );

      if (oldT && oldT.weight !== newT.weight) {
        return {
          name: newT.displayName || newT.name || '',
          oldWeight: oldT.weight,
          newWeight: newT.weight,
        };
      }
      return null;
    })
    .filter(Boolean);

  const variantChanges = newTreatments
    .map(newT => {
      const oldT = oldTreatments.find(
        t => getTreatmentKey(t) === getTreatmentKey(newT),
      );
      if (oldT && oldT.displayName !== newT.displayName) {
        return {
          oldDisplayName: oldT.displayName || '',
          newDisplayName: newT.displayName || '',
        };
      }
      return null;
    })
    .filter(Boolean);

  const descriptionChanges = newTreatments
    .map(newT => {
      const oldT = oldTreatments.find(
        t => getTreatmentKey(t) === getTreatmentKey(newT),
      );
      const oldDesc = oldT?.description ?? '';
      const newDesc = newT.description ?? '';
      if (oldT && oldDesc !== newDesc) {
        return {
          name: newT.displayName || newT.name || '',
          oldDescription: oldDesc || undefined,
          newDescription: newDesc || undefined,
        };
      }
      return null;
    })
    .filter(Boolean);

  const oldFlag = oldValue?.flag;
  const newFlag = newValue?.flag;
  const flagAdded = !oldFlag && !!newFlag;
  const flagRemoved = !!oldFlag && !newFlag;
  const flagChanged = !!oldFlag && !!newFlag && oldFlag !== newFlag;

  return (
    <Fragment>
      {flagAdded && (
        <ActivityItem.Section label="Added flag">
          <ActivityTagItem
            tagLabel={getFlagDisplayName(newFlag)}
            ariaLabel="Flag name"
          />
        </ActivityItem.Section>
      )}
      {flagRemoved && (
        <ActivityItem.Section label="Removed flag">
          <ActivityTagItem
            tagLabel={getFlagDisplayName(oldFlag)}
            ariaLabel="Flag name"
          />
        </ActivityItem.Section>
      )}
      {flagChanged && (
        <ActivityItem.Section label="Changed flag">
          <ActivityTagItem
            oldValue={getFlagDisplayName(oldFlag)}
            newValue={getFlagDisplayName(newFlag)}
            ariaLabel="Flag name"
          />
        </ActivityItem.Section>
      )}

      {addedTreatments.length > 0 && (
        <ActivityItem.Section
          label={`Added ${pluralize(addedTreatments.length, 'treatment')}`}
        >
          {addedTreatments.map(treatment => (
            <div className={classes.container}>
              <ActivityTagItem
                key={getTreatmentKey(treatment)}
                tagLabel={treatment.displayName || treatment.name || ''}
                newValue={`${treatment.weight / 100}%`}
                ariaLabel="Treatment name"
              />
            </div>
          ))}
        </ActivityItem.Section>
      )}

      {removedTreatments.length > 0 && (
        <ActivityItem.Section
          label={`Removed ${pluralize(removedTreatments.length, 'treatment')}`}
        >
          <div className={classNames(classes.container)}>
            {removedTreatments.map(treatment => (
              <ActivityTagItem
                key={getTreatmentKey(treatment)}
                tagLabel={treatment.displayName || treatment.name || ''}
                oldValue={`${treatment.weight / 100}%`}
                ariaLabel="Treatment name"
              />
            ))}
          </div>
        </ActivityItem.Section>
      )}

      {weightChanges.length > 0 && (
        <ActivityItem.Section
          label={`Updated treatment ${pluralize(weightChanges.length, 'weight')}`}
        >
          {weightChanges.map(newChange => (
            <div className={classes.container}>
              <ActivityTagItem
                key={newChange!.name}
                tagLabel={newChange!.name}
                oldValue={`${newChange!.oldWeight / 100}%`}
                newValue={`${newChange!.newWeight / 100}%`}
                ariaLabel="Treatment name"
              />
            </div>
          ))}
        </ActivityItem.Section>
      )}

      {variantChanges.length > 0 && (
        <ActivityItem.Section
          label={`Updated treatment ${pluralize(variantChanges.length, 'variant')}`}
        >
          {variantChanges.map(variantChange => (
            <div
              key={`${variantChange!.oldDisplayName}-${variantChange!.newDisplayName}`}
              className={classes.container}
            >
              <ActivityTagItem
                oldValue={variantChange!.oldDisplayName}
                newValue={variantChange!.newDisplayName}
                ariaLabel="Treatment variant"
              />
            </div>
          ))}
        </ActivityItem.Section>
      )}

      {descriptionChanges.length > 0 && (
        <ActivityItem.Section
          label={`Updated treatment ${pluralize(descriptionChanges.length, 'description')}`}
        >
          {descriptionChanges.map(descChange => (
            <div key={descChange!.name} className={classes.container}>
              <ActivityTagItem
                tagLabel={descChange!.name}
                oldValue={descChange!.oldDescription}
                newValue={descChange!.newDescription}
                ariaLabel="Treatment description"
              />
            </div>
          ))}
        </ActivityItem.Section>
      )}
    </Fragment>
  );
};

export interface CreateTreatmentsChangeExtensionOptions {
  id: string;
  fieldPath: string;
}

const hasVisibleChanges = (change: {
  oldValue?: unknown;
  newValue?: unknown;
}): boolean => {
  const oldValue = change.oldValue as TreatmentsData | null;
  const newValue = change.newValue as TreatmentsData | null;

  const oldFlag = oldValue?.flag;
  const newFlag = newValue?.flag;
  if (oldFlag !== newFlag) return true;

  const oldTreatments = oldValue?.treatments ?? [];
  const newTreatments = newValue?.treatments ?? [];

  const { added, removed } = diffItems(
    oldTreatments,
    newTreatments,
    getTreatmentKey,
  );
  if (added.length > 0 || removed.length > 0) return true;

  return newTreatments.some(newT => {
    const oldT = oldTreatments.find(
      t => getTreatmentKey(t) === getTreatmentKey(newT),
    );
    return (
      oldT &&
      (oldT.weight !== newT.weight ||
        oldT.displayName !== newT.displayName ||
        (oldT.description ?? '') !== (newT.description ?? ''))
    );
  });
};

export const createTreatmentsChangeExtension = (
  options: CreateTreatmentsChangeExtensionOptions,
) => {
  return createWorkflowUpdatesChangeExtension({
    id: options.id,
    predicate: ({ change }) =>
      change.fieldPath === options.fieldPath && hasVisibleChanges(change),
    render: TreatmentChangeRender,
  });
};
